import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useStore } from "../../js/state/store";
import {
  billingSummaryForSession,
  CANCEL_NOTICE_HOURS,
  formatMoney,
  formatRenewalDate,
  isLocalSandbox,
  requestCancelAtPeriodEnd,
  runSandboxCheckout,
  runSandboxRenewal,
  subscribeAccountsListener,
  undoCancelAtPeriodEnd,
} from "../../js/billing/billingDesk";
import { payUrl, JOURNEY_LINK_PROPS } from "../../js/utils/siteLinks";

export default function SandboxBillingPanel({ session, onFlash }) {
  const { dispatch } = useStore();
  const [summary, setSummary] = useState(() => billingSummaryForSession(session));
  const [busy, setBusy] = useState("");
  const sandbox = isLocalSandbox();

  useEffect(() => {
    setSummary(billingSummaryForSession(session));
    return subscribeAccountsListener(() => setSummary(billingSummaryForSession(session)));
  }, [session]);

  function refresh(nextSession) {
    if (nextSession) dispatch({ type: "SET_SESSION", payload: nextSession });
    setSummary(billingSummaryForSession(nextSession || session));
  }

  async function handleSandboxCheckout() {
    setBusy("checkout");
    try {
      const { result, session: nextSession } = runSandboxCheckout(session);
      refresh(nextSession);
      onFlash?.(
        result?.duplicate
          ? "That sandbox checkout was already recorded."
          : "Sandbox checkout recorded. Subscription is active — no card was charged."
      );
    } catch (error) {
      onFlash?.(error.message || "Sandbox checkout did not complete.");
    } finally {
      setBusy("");
    }
  }

  async function handleSandboxRenewal(force) {
    setBusy(force ? "renew-force" : "renew");
    try {
      const result = runSandboxRenewal(session, { force });
      if (result?.session) {
        refresh(result.session);
      } else {
        setSummary(billingSummaryForSession(session));
      }
      if (result?.cancelled) {
        onFlash?.("Renewal date reached with cancellation scheduled — subscription ended.");
      } else if (result?.skipped) {
        onFlash?.(`Renewal is not due until ${formatRenewalDate(result.renewal_date)}.`);
      } else {
        onFlash?.("Sandbox renewal recorded. Renewal date advanced by one billing period.");
      }
    } catch (error) {
      onFlash?.(error.message || "Renewal simulation failed.");
    } finally {
      setBusy("");
    }
  }

  function handleCancel() {
    const reason = window.prompt(
      `Cancel at end of the current period? Billing stops at least ${CANCEL_NOTICE_HOURS} hours before renewal. Optional note:`
    );
    if (reason == null) return;
    setBusy("cancel");
    try {
      requestCancelAtPeriodEnd(session, reason);
      setSummary(billingSummaryForSession(session));
      onFlash?.("Cancellation scheduled for the end of this billing period.");
    } catch (error) {
      onFlash?.(error.message || "Could not schedule cancellation.");
    } finally {
      setBusy("");
    }
  }

  function handleUndoCancel() {
    setBusy("undo-cancel");
    try {
      undoCancelAtPeriodEnd(session);
      setSummary(billingSummaryForSession(session));
      onFlash?.("Cancellation removed. Your desk renews on the usual date.");
    } catch (error) {
      onFlash?.(error.message || "Could not restore renewal.");
    } finally {
      setBusy("");
    }
  }

  if (!summary) {
    return (
      <article className="card account-card">
        <h2>Subscription / Billing</h2>
        <p className="muted">Sign in as an agent or broker to manage billing.</p>
      </article>
    );
  }

  return (
    <article className="card account-card sandbox-billing">
      <header className="sandbox-billing__head">
        <div>
          <h2>Subscription / Billing</h2>
          <p className="page-lead">
            PayPal sandbox checkout for real card tests. Local sandbox buttons simulate payment without
            charging a card.
          </p>
        </div>
        <span className="sandbox-billing__badge">{sandbox ? "Sandbox · local" : "PayPal sandbox"}</span>
      </header>

      <dl className="sandbox-billing__grid">
        <div>
          <dt>Product</dt>
          <dd>{summary.productLabel}</dd>
        </div>
        <div>
          <dt>Plan</dt>
          <dd>{summary.planLabel}</dd>
        </div>
        <div>
          <dt>Status</dt>
          <dd>
            <span className={`sandbox-billing__status is-${summary.status}`}>{summary.statusLabel}</span>
          </dd>
        </div>
        <div>
          <dt>Renewal</dt>
          <dd>{formatRenewalDate(summary.renewalDate)}</dd>
        </div>
        <div>
          <dt>Amount</dt>
          <dd>
            {summary.amount
              ? `${formatMoney(summary.amount)} / ${summary.interval === "year" ? "year" : "month"}`
              : "Comped"}
          </dd>
        </div>
        <div>
          <dt>Payment method</dt>
          <dd className="sandbox-billing__mono">{summary.paymentMethod}</dd>
        </div>
      </dl>

      {summary.billing?.frozen ? (
        <p className="confirm-banner sandbox-billing__notice">
          Billing is frozen{summary.billing.reason ? `: ${summary.billing.reason}` : ""}. Original renewal
          anchor: {formatRenewalDate(summary.billing.anchorDate)}. Resume the desk or pay to restore service.
        </p>
      ) : null}

      {summary.cancelAtPeriodEnd ? (
        <p className="confirm-banner sandbox-billing__notice">
          Cancellation scheduled for {formatRenewalDate(summary.cancelEffectiveAt)}. Desk access continues
          until then. Cancel at least {CANCEL_NOTICE_HOURS} hours before renewal to avoid the next charge.
        </p>
      ) : null}

      <div className="sandbox-billing__actions">
        <a className="primary-btn" href={payUrl(session)} {...JOURNEY_LINK_PROPS}>
          Open PayPal checkout
        </a>
        {sandbox ? (
          <button
            type="button"
            className="ghost-btn"
            disabled={Boolean(busy)}
            onClick={handleSandboxCheckout}
          >
            {busy === "checkout" ? "Recording…" : "Simulate checkout (no card)"}
          </button>
        ) : null}
        {summary.cancelAtPeriodEnd ? (
          <button type="button" className="ghost-btn" disabled={Boolean(busy)} onClick={handleUndoCancel}>
            Keep subscription
          </button>
        ) : (
          <button type="button" className="ghost-btn" disabled={Boolean(busy)} onClick={handleCancel}>
            Cancel at period end
          </button>
        )}
        {sandbox ? (
          <>
            <button
              type="button"
              className="ghost-btn"
              disabled={Boolean(busy)}
              onClick={() => handleSandboxRenewal(false)}
            >
              {busy === "renew" ? "Checking…" : "Simulate renewal (if due)"}
            </button>
            <button
              type="button"
              className="ghost-btn"
              disabled={Boolean(busy)}
              onClick={() => handleSandboxRenewal(true)}
            >
              {busy === "renew-force" ? "Running…" : "Force renewal now"}
            </button>
          </>
        ) : null}
        <Link className="ghost-btn" to="/onboarding/subscribe">
          Activation step
        </Link>
      </div>

      <section className="sandbox-billing__history">
        <h3>Recent invoices</h3>
        {summary.invoices.length ? (
          <ul className="sandbox-billing__list">
            {summary.invoices.map((row) => (
              <li key={row.id}>
                <span>{row.number}</span>
                <span>{formatMoney(row.amount)}</span>
                <span>{row.status}</span>
                <span className="muted">{formatRenewalDate(row.paid_at || row.issued_at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">No invoices yet. Complete checkout to create the first receipt.</p>
        )}
      </section>

      <section className="sandbox-billing__history">
        <h3>Recent payments</h3>
        {summary.payments.length ? (
          <ul className="sandbox-billing__list">
            {summary.payments.map((row) => (
              <li key={row.id}>
                <span>{row.note || row.method_ref}</span>
                <span>{formatMoney(row.amount)}</span>
                <span>{row.result}</span>
                <span className="muted">{formatRenewalDate(row.at)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted">Payments appear here after checkout or renewal.</p>
        )}
      </section>

      <p className="muted sandbox-billing__fine">
        Sandbox mode uses PayPal&apos;s test environment when credentials are configured. Local simulate
        buttons write to this device only — use them for Step 11 payment tests without live charges.
        Full account closure stays under{" "}
        <Link to="/account?tab=lifecycle&card=cancel">Account Lifecycle</Link>.
      </p>
    </article>
  );
}
